import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import "../../wallet.css";
import { Pill } from "./walletShared";

/**
 * Maintain Mobile App (driver frontend).
 *
 * Upload a driver-app build, and the backend mints a permanent share link for that exact version
 * — supervisors forward it to drivers, who open it in any phone browser and install. Releasing a
 * newer build does not break the old links: they keep resolving but lead with the current build,
 * with an opt-in path to the older one (unless that has been switched off here).
 *
 * Min / max version drive what the app itself does on launch: below the minimum it must update
 * before carrying on; below the maximum it is merely nudged.
 *
 * API: {REACT_APP_API_URL}shypvdriverapp/mobile-app
 */

const PLATFORMS = [
  { key: "ANDROID", label: "Android", icon: "fa-android", ext: ".apk,.aab" },
  { key: "IOS", label: "iOS", icon: "fa-apple", ext: ".ipa" },
];

const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export default function MobileAppVersions() {
  const API_URL = process.env.REACT_APP_API_URL; // driver API, trailing slash
  const API = `${API_URL}shypvdriverapp/mobile-app`;

  const role = sessionStorage.getItem("role");
  const canManage = ["super_admin", "admin", "support"].includes(role);

  const [platform, setPlatform] = useState("ANDROID");
  const [versions, setVersions] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);

  // upload form
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState("");
  const [versionCode, setVersionCode] = useState("");
  const [releaseNotes, setReleaseNotes] = useState("");
  const [storeUrl, setStoreUrl] = useState("");
  const [forceUpdate, setForceUpdate] = useState(false);
  const [markLatest, setMarkLatest] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  // config form (kept separate so typing does not fight the loaded values)
  const [minVersion, setMinVersion] = useState("");
  const [maxVersion, setMaxVersion] = useState("");
  const [updateMessage, setUpdateMessage] = useState("");
  const [blockOldLinks, setBlockOldLinks] = useState(true);
  const [allowOldDownload, setAllowOldDownload] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  const meta = PLATFORMS.find((p) => p.key === platform) || PLATFORMS[0];

  const notify = (type, text) => {
    setBanner({ type, text });
    setTimeout(() => setBanner(null), 5000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, cRes] = await Promise.all([
        axios.get(`${API}/versions`, { params: { platform } }),
        axios.get(`${API}/config`, { params: { platform } }),
      ]);
      setVersions((vRes.data && vRes.data.data) || []);
      const cfg = (cRes.data && cRes.data.data) || null;
      setConfig(cfg);
      if (cfg) {
        setMinVersion(cfg.minVersion || "");
        setMaxVersion(cfg.maxVersion || "");
        setUpdateMessage(cfg.updateMessage || "");
        setBlockOldLinks(cfg.blockOldLinks !== false);
        setAllowOldDownload(cfg.allowOldDownload !== false);
      }
    } catch (err) {
      notify("danger", "Could not load app versions: " + (err?.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [API, platform]);

  useEffect(() => {
    load();
  }, [load]);

  const resetUploadForm = () => {
    setFile(null);
    setVersion("");
    setVersionCode("");
    setReleaseNotes("");
    setStoreUrl("");
    setForceUpdate(false);
    setMarkLatest(true);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const doUpload = async () => {
    if (!version.trim()) {
      notify("danger", "Enter the version number, e.g. 2.4.1");
      return;
    }
    if (!file && !storeUrl.trim()) {
      notify("danger", "Choose a build file, or give a store URL for a store-hosted release");
      return;
    }

    const form = new FormData();
    if (file) form.append("file", file);
    form.append("platform", platform);
    form.append("version", version.trim());
    if (versionCode.trim()) form.append("versionCode", versionCode.trim());
    if (releaseNotes.trim()) form.append("releaseNotes", releaseNotes.trim());
    if (storeUrl.trim()) form.append("storeUrl", storeUrl.trim());
    form.append("forceUpdate", forceUpdate);
    form.append("markLatest", markLatest);
    const who = sessionStorage.getItem("name") || sessionStorage.getItem("username") || "";
    if (who) form.append("uploadedBy", who);

    setUploading(true);
    setProgress(0);
    try {
      const res = await axios.post(`${API}/versions`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      const uploaded = res?.data?.data;
      notify("success", `Version ${uploaded?.versionName || version} uploaded. Share link is ready.`);
      resetUploadForm();
      load();
    } catch (err) {
      notify("danger", "Upload failed: " + (err?.response?.data?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  const patchVersion = async (row, changes, successText) => {
    setBusyId(row.id);
    try {
      await axios.patch(`${API}/versions/${row.id}`, changes);
      notify("success", successText);
      load();
    } catch (err) {
      notify("danger", err?.response?.data?.message || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const removeVersion = async (row) => {
    if (!window.confirm(`Delete version ${row.versionName}? Its share link will stop working.`)) return;
    setBusyId(row.id);
    try {
      await axios.delete(`${API}/versions/${row.id}`);
      notify("success", `Version ${row.versionName} deleted`);
      load();
    } catch (err) {
      notify("danger", err?.response?.data?.message || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${API}/config`, {
        platform,
        minVersion: minVersion.trim(),
        maxVersion: maxVersion.trim(),
        updateMessage: updateMessage.trim(),
        blockOldLinks,
        allowOldDownload,
        updatedBy: sessionStorage.getItem("name") || "",
      });
      notify("success", "Settings saved");
      load();
    } catch (err) {
      notify("danger", err?.response?.data?.message || err.message);
    } finally {
      setSavingConfig(false);
    }
  };

  const copyLink = async (row) => {
    const url = row.shareUrl;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        // http:// dev hosts have no clipboard API — fall back to a hidden textarea.
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedToken(row.downloadToken);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (err) {
      window.prompt("Copy this link:", url);
    }
  };

  const shareOnWhatsapp = (row) => {
    const text =
      `Shypv Driver App ${row.versionName} is available.\n` +
      `Download and install: ${row.shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const latest = versions.find((v) => v.isLatest);

  return (
    <div className="wallet-page">
      <div className="w-topbar">
        <div className="w-topbar-left">
          <div className="w-icon-badge"><i className="fa fa-mobile" aria-hidden="true" /></div>
          <div>
            <h2 className="w-title">Maintain Mobile App</h2>
            <p className="w-subtitle">
              Upload a new build, share its download link, and set the minimum / maximum version the
              app is allowed to run.
            </p>
          </div>
        </div>
        <div className="w-topbar-actions">
          {PLATFORMS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`w-btn ${platform === p.key ? "w-btn-primary" : "w-btn-ghost"}`}
              onClick={() => setPlatform(p.key)}
            >
              <i className={`fa ${p.icon}`} aria-hidden="true" /> {p.label}
            </button>
          ))}
        </div>
      </div>

      {banner && (
        <div className={`w-pill w-pill-${banner.type}`} style={{ marginBottom: 14 }}>{banner.text}</div>
      )}

      {/* Current release ------------------------------------------------- */}
      <div className="w-card">
        <div className="w-card-header">
          <span className="w-card-title"><i className="fa fa-rocket" aria-hidden="true" /> Current release</span>
          {latest && <span className="w-card-meta">{latest.downloadCount || 0} downloads</span>}
        </div>
        <div className="w-card-body">
          {loading && !latest ? (
            <div className="w-loading"><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Loading…</div>
          ) : latest ? (
            <div className="w-form-row" style={{ alignItems: "center", gap: 14 }}>
              <div style={{ flex: "1 1 220px" }}>
                <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
                  {latest.versionName}
                  {latest.versionCode ? <span style={{ fontSize: 14, color: "#8a93a8", fontWeight: 500 }}> &nbsp;build {latest.versionCode}</span> : null}
                </div>
                <div style={{ fontSize: 13, color: "#66708a", marginTop: 4 }}>
                  {meta.label} · {latest.fileSizeLabel || "store hosted"} · released {fmtDate(latest.createdAt)}
                </div>
              </div>
              <div style={{ flex: "0 0 auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" className="w-btn w-btn-ghost w-btn-sm" onClick={() => copyLink(latest)}>
                  <i className={`fa ${copiedToken === latest.downloadToken ? "fa-check" : "fa-link"}`} aria-hidden="true" />{" "}
                  {copiedToken === latest.downloadToken ? "Copied" : "Copy link"}
                </button>
                <button type="button" className="w-btn w-btn-ghost w-btn-sm" onClick={() => shareOnWhatsapp(latest)}>
                  <i className="fa fa-whatsapp" aria-hidden="true" /> Share
                </button>
                <a
                  className="w-btn w-btn-primary w-btn-sm"
                  style={{ textDecoration: "none" }}
                  href={latest.shareUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fa fa-external-link" aria-hidden="true" /> Open page
                </a>
              </div>
            </div>
          ) : (
            <div className="w-empty">
              No {meta.label} build has been released yet. Upload one below to create the first share link.
            </div>
          )}
        </div>
      </div>

      {/* Upload ----------------------------------------------------------- */}
      {canManage && (
        <div className="w-card">
          <div className="w-card-header">
            <span className="w-card-title"><i className="fa fa-cloud-upload" aria-hidden="true" /> Upload new version</span>
          </div>
          <div className="w-card-body">
            <div className="w-form-row" style={{ alignItems: "flex-end" }}>
              <div className="w-field" style={{ flex: "0 0 160px" }}>
                <label className="w-field-label">Version *</label>
                <input
                  type="text"
                  className="w-input"
                  placeholder="2.4.1"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                />
              </div>
              <div className="w-field" style={{ flex: "0 0 140px" }}>
                <label className="w-field-label">Build number</label>
                <input
                  type="number"
                  className="w-input"
                  placeholder="241"
                  value={versionCode}
                  onChange={(e) => setVersionCode(e.target.value)}
                />
              </div>
              <div className="w-field" style={{ flex: "1 1 260px" }}>
                <label className="w-field-label">Build file</label>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <label className="w-btn w-btn-ghost w-btn-sm w-file-btn">
                    <i className="fa fa-upload" aria-hidden="true" /> Choose {meta.key === "ANDROID" ? "APK" : "IPA"}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={meta.ext}
                      onChange={(e) => setFile(e.target.files && e.target.files[0])}
                      style={{ display: "none" }}
                    />
                  </label>
                  <span className="w-file-name">
                    {file ? `${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)` : "No file selected"}
                  </span>
                </div>
              </div>
            </div>

            <div className="w-form-row" style={{ alignItems: "flex-end", marginTop: 14 }}>
              <div className="w-field" style={{ flex: "1 1 100%" }}>
                <label className="w-field-label">What's new (shown on the download page)</label>
                <textarea
                  className="w-textarea"
                  rows={3}
                  placeholder="Fixed COD collection screen, faster scanning…"
                  value={releaseNotes}
                  onChange={(e) => setReleaseNotes(e.target.value)}
                />
              </div>
            </div>

            <div className="w-form-row" style={{ alignItems: "flex-end", marginTop: 4 }}>
              <div className="w-field" style={{ flex: "1 1 320px" }}>
                <label className="w-field-label">Store URL (optional — for Play Store / App Store releases)</label>
                <input
                  type="text"
                  className="w-input"
                  placeholder="https://play.google.com/store/apps/details?id=…"
                  value={storeUrl}
                  onChange={(e) => setStoreUrl(e.target.value)}
                />
              </div>
            </div>

            <div className="w-form-row" style={{ alignItems: "center", marginTop: 14, gap: 24 }}>
              <label className="w-toggle-row" style={{ margin: 0, flex: "0 0 auto" }}>
                <span className="w-toggle">
                  <input type="checkbox" checked={markLatest} onChange={(e) => setMarkLatest(e.target.checked)} />
                  <span className="w-toggle-slider" />
                </span>
                <span className="w-toggle-label">Make this the current release</span>
              </label>
              <label className="w-toggle-row" style={{ margin: 0, flex: "0 0 auto" }}>
                <span className="w-toggle">
                  <input type="checkbox" checked={forceUpdate} onChange={(e) => setForceUpdate(e.target.checked)} />
                  <span className="w-toggle-slider" />
                </span>
                <span className="w-toggle-label">Mandatory update</span>
              </label>
              <div style={{ flex: "1 1 auto" }} />
              <button type="button" className="w-btn w-btn-primary" disabled={uploading} onClick={doUpload}>
                {uploading ? `Uploading… ${progress}%` : "Upload version"}
              </button>
            </div>

            {uploading && (
              <div style={{ height: 6, borderRadius: 99, background: "#eef1f6", marginTop: 14, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${progress}%`, background: "#00a6ed", transition: "width .2s" }} />
              </div>
            )}

            <p className="w-help-text" style={{ marginTop: 12 }}>
              Making this the current release moves everyone onto it: the home banner announces it and
              links to older builds start recommending it instead. Mandatory update also blocks the
              older app from carrying on until the driver updates.
            </p>
          </div>
        </div>
      )}

      {/* Version policy --------------------------------------------------- */}
      <div className="w-card">
        <div className="w-card-header">
          <span className="w-card-title"><i className="fa fa-shield" aria-hidden="true" /> Supported versions</span>
          {config && config.maxVersion && <span className="w-card-meta">current target {config.maxVersion}</span>}
        </div>
        <div className="w-card-body">
          <div className="w-form-row" style={{ alignItems: "flex-end" }}>
            <div className="w-field" style={{ flex: "0 0 190px" }}>
              <label className="w-field-label">Minimum version</label>
              <input
                type="text"
                className="w-input"
                placeholder="2.0.0"
                value={minVersion}
                onChange={(e) => setMinVersion(e.target.value)}
                disabled={!canManage}
              />
            </div>
            <div className="w-field" style={{ flex: "0 0 190px" }}>
              <label className="w-field-label">Maximum version</label>
              <input
                type="text"
                className="w-input"
                placeholder="2.4.1"
                value={maxVersion}
                onChange={(e) => setMaxVersion(e.target.value)}
                disabled={!canManage}
              />
            </div>
            <div className="w-field" style={{ flex: "1 1 300px" }}>
              <label className="w-field-label">Update message</label>
              <input
                type="text"
                className="w-input"
                placeholder="A new version of the driver app is available. Please update."
                value={updateMessage}
                onChange={(e) => setUpdateMessage(e.target.value)}
                disabled={!canManage}
              />
            </div>
          </div>

          <div className="w-form-row" style={{ alignItems: "center", marginTop: 16, gap: 24 }}>
            <label className="w-toggle-row" style={{ margin: 0, flex: "0 0 auto" }}>
              <span className="w-toggle">
                <input
                  type="checkbox"
                  checked={blockOldLinks}
                  onChange={(e) => setBlockOldLinks(e.target.checked)}
                  disabled={!canManage}
                />
                <span className="w-toggle-slider" />
              </span>
              <span className="w-toggle-label">Warn on links to older builds</span>
            </label>
            <label className="w-toggle-row" style={{ margin: 0, flex: "0 0 auto" }}>
              <span className="w-toggle">
                <input
                  type="checkbox"
                  checked={allowOldDownload}
                  onChange={(e) => setAllowOldDownload(e.target.checked)}
                  disabled={!canManage || !blockOldLinks}
                />
                <span className="w-toggle-slider" />
              </span>
              <span className="w-toggle-label">…but still let them download it</span>
            </label>
            <div style={{ flex: "1 1 auto" }} />
            {canManage && (
              <button type="button" className="w-btn w-btn-primary" disabled={savingConfig} onClick={saveConfig}>
                {savingConfig ? "Saving…" : "Save settings"}
              </button>
            )}
          </div>

          <p className="w-help-text" style={{ marginTop: 12 }}>
            Below the <strong>minimum</strong> the app must update before it can be used. Below the{" "}
            <strong>maximum</strong> it is only prompted. The maximum follows whichever build is
            marked current, but you can point it back at an earlier one if a release turns out bad.
          </p>
        </div>
      </div>

      {/* All versions ------------------------------------------------------ */}
      <div className="w-card">
        <div className="w-card-header">
          <span className="w-card-title"><i className="fa fa-history" aria-hidden="true" /> All versions</span>
          {versions.length > 0 && <span className="w-card-meta">{versions.length} uploaded</span>}
        </div>
        <div className="w-card-body">
          {loading && versions.length === 0 ? (
            <div className="w-loading"><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Loading…</div>
          ) : versions.length === 0 ? (
            <div className="w-empty">Nothing uploaded for {meta.label} yet.</div>
          ) : (
            <div className="w-scan-list" style={{ maxHeight: "none" }}>
              {versions.map((row) => {
                const blocked = row.status === "BLOCKED";
                return (
                  <div key={row.id} className="w-scan-row" style={{ flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                    <span className="w-scan-awb" style={{ minWidth: 96 }}>{row.versionName}</span>

                    <span style={{ flex: "0 0 auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {row.isLatest && <Pill tone="success" icon="fa-check-circle">Current</Pill>}
                      {blocked && <Pill tone="danger" icon="fa-ban">Blocked</Pill>}
                      {!row.isLatest && !blocked && <Pill tone="neutral" icon="fa-clock-o">Superseded</Pill>}
                      {row.forceUpdate && <Pill tone="warning" icon="fa-exclamation-triangle">Mandatory</Pill>}
                    </span>

                    <span style={{ flex: "1 1 180px", fontSize: 13, color: "#66708a" }}>
                      {row.fileSizeLabel || "store hosted"} · {fmtDate(row.createdAt)} ·{" "}
                      {row.downloadCount || 0} downloads
                      {row.uploadedBy ? ` · by ${row.uploadedBy}` : ""}
                    </span>

                    <span style={{ flex: "0 0 auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="w-btn w-btn-ghost w-btn-sm"
                        onClick={() => copyLink(row)}
                        title={row.shareUrl}
                      >
                        <i className={`fa ${copiedToken === row.downloadToken ? "fa-check" : "fa-link"}`} aria-hidden="true" />{" "}
                        {copiedToken === row.downloadToken ? "Copied" : "Link"}
                      </button>
                      <button type="button" className="w-btn w-btn-ghost w-btn-sm" onClick={() => shareOnWhatsapp(row)}>
                        <i className="fa fa-whatsapp" aria-hidden="true" />
                      </button>

                      {canManage && !row.isLatest && !blocked && (
                        <button
                          type="button"
                          className="w-btn w-btn-ghost w-btn-sm"
                          disabled={busyId === row.id}
                          onClick={() => patchVersion(row, { isLatest: true }, `Version ${row.versionName} is now the current release`)}
                        >
                          <i className="fa fa-rocket" aria-hidden="true" /> Make current
                        </button>
                      )}

                      {canManage && (
                        <button
                          type="button"
                          className={`w-btn w-btn-sm ${blocked ? "w-btn-ghost" : "w-btn-danger"}`}
                          disabled={busyId === row.id || row.isLatest}
                          title={row.isLatest ? "The current release cannot be blocked" : ""}
                          onClick={() =>
                            patchVersion(
                              row,
                              { status: blocked ? "ACTIVE" : "BLOCKED" },
                              blocked ? `Version ${row.versionName} unblocked` : `Version ${row.versionName} blocked`
                            )
                          }
                        >
                          <i className={`fa ${blocked ? "fa-undo" : "fa-ban"}`} aria-hidden="true" />{" "}
                          {blocked ? "Unblock" : "Block"}
                        </button>
                      )}

                      {canManage && (
                        <button
                          type="button"
                          className="w-btn w-btn-ghost w-btn-sm"
                          disabled={busyId === row.id || row.isLatest}
                          title={row.isLatest ? "The current release cannot be deleted" : "Delete"}
                          onClick={() => removeVersion(row)}
                        >
                          <i className="fa fa-trash" aria-hidden="true" />
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <p className="w-help-text" style={{ marginTop: 14 }}>
            Every version keeps its own link, so a link a supervisor sent last month still resolves to
            that exact build. <strong>Block</strong> stops a build being installed at all; a
            superseded build stays installable unless you turn that off above.
          </p>
        </div>
      </div>
    </div>
  );
}
